import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";

// Component imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventInputScroll from "hooks/usePreventInputScroll";

// CSS imports
import 'styles/Font.css';
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";

function PtInquiry() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();
    const navigate = useNavigate();
    const location = useLocation();

    const { mnKey, trainerName } = location.state || {};

    const [step, setStep] = useState(1);

    // --- State: API에서 받아온 선택지 목록 ---
    const [purposeOptions, setPurposeOptions] = useState([]); // 운동 목표 목록
    const [timeOptions, setTimeOptions] = useState([]);       // 운동 시간 목록

    // --- State: 사용자 입력 데이터 ---
    const [formData, setFormData] = useState({
        goal: [],
        time: [],
        message: ''
    });

    // 모달 상태
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);


    // --- [API] 초기 코드 데이터 로드 (수정됨) ---
    useEffect(() => {
        const fetchCodes = async () => {
            try {
                const { token } = getTokenStatus();

                // 공통 코드 조회 API 호출 (GET)
                // API 엔드포인트가 /code 라고 가정 (필요 시 /api/code 등으로 수정)
                const response = await fetch(`${API_BASE_URL}/code`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`,
                    }
                });

                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const res = await response.json();
                const data = res.data || res; // 응답 구조에 따라 조정

                // 받아온 데이터로 상태 업데이트
                if (data) {
                    if (data.goal) setPurposeOptions(data.goal);
                    if (data.time) setTimeOptions(data.time);
                }

            } catch (e) {
                console.error("Code Fetch Error:", e);
                // 에러 시 빈 배열 유지 또는 재시도 로직
            }
        };

        fetchCodes();
    }, []);


    // --- Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // 운동 목표 선택
    const handlePurposeSelect = (item) => {
        setFormData(prev => {
            const current = prev.goal;
            const exists = current.find(i => i.code === item.code);

            if (exists) {
                return { ...prev, goal: current.filter(i => i.code !== item.code) };
            } else {
                return { ...prev, goal: [...current, item] };
            }
        });
    };

    // 운동 시간 선택
    const handleTimeSelect = (item) => {
        setFormData(prev => {
            const current = prev.time;
            const exists = current.find(i => i.code === item.code);

            if (exists) {
                return { ...prev, time: current.filter(i => i.code !== item.code) };
            } else {
                return { ...prev, time: [...current, item] };
            }
        });
    };

    const showModal = (title, message, onConfirm = null) => {
        setModalConfig({ isOpen: true, title, message, onConfirm });
    };

    const handleCloseModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
    };

    const handleConfirmModal = () => {
        if (modalConfig.onConfirm) modalConfig.onConfirm();
        handleCloseModal();
    };

    const handleNext = () => {
        if (step === 1) {
            if (formData.goal.length === 0) {
                showModal("알림", "운동 목표를 하나 이상 선택해주세요.");
                return;
            }
            setStep(2);
        } else if (step === 2) {
            if (formData.time.length === 0) {
                showModal("알림", "원하시는 시간을 하나 이상 선택해주세요.");
                return;
            }
            setStep(3);
        }
    };

    // 최종 제출 및 API 호출 핸들러
    const handleSubmit = async () => {
        /* 문의 등록 API 개발 후 주석 해제하여 사용 */
        // 전송 데이터 구성
        const payload = {
            mnKey: mnKey,
            goal: JSON.stringify(formData.goal),
            time: JSON.stringify(formData.time),
            consideration: JSON.stringify(formData.message),
        };


        try {
            const { token } = getTokenStatus();

            // API 호출
            const response = await fetch(`${API_BASE_URL}/reginquiry`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });
            const resData = await response.json();

            // 응답 처리
            if (response.ok && (resData.code === 200 || resData.status === 'success')) {
                // 성공 시 모달 띄우고 홈(또는 PT 메인)으로 이동
                showModal("문의 접수", "문의가 성공적으로 접수되었습니다.", () => {
                    navigate('/pt', { replace: true });
                });
            } else {
                // 실패 시 메시지 표시
                const msg = resData.message || "문의 접수에 실패했습니다.";
                showModal("접수 실패", msg);
            }
        } catch (e) {
            console.error("Submit Error:", e);
            showModal("오류", "서버 통신 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        }

        // 임시 알림
        // showModal("안내", "현재 문의하기 기능은 준비 중입니다.");
    };


    // --- UI 관련 ---
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
        };
        measureHeight();
        window.addEventListener('resize', measureHeight);
        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    const dynamicMainContentStyle = { ...staticStyles.mainContent, paddingTop: `${headerHeight}px` };
    const getStepStyle = (stepNum) => ({ ...staticStyles.stepCircle, ...(step >= stepNum ? staticStyles.stepCircleActive : staticStyles.stepCircleInactive) });

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title="" />
            </div>

            <main style={dynamicMainContentStyle}>
                <div style={staticStyles.stepIndicatorContainer}>
                    <div style={getStepStyle(1)}>1</div>
                    <div style={staticStyles.stepLine} />
                    <div style={getStepStyle(2)}>2</div>
                    <div style={staticStyles.stepLine} />
                    <div style={getStepStyle(3)}>3</div>
                </div>

                <div style={staticStyles.stepTitle}>
                    {[
                        "",
                        <span key="s1">트레이너와 함께<br />이루고 싶은 목표를 선택해 주세요</span>,
                        "트레이닝 받고싶은 시간을 선택해주세요",
                        "트레이닝 전 트레이너가 알아야 할 유의사항을 적어주세요"
                    ][step]}
                </div>

                {/* STEP 1: 운동 목표 */}
                {step === 1 && (
                    <div style={staticStyles.formContainer}>
                        <div style={staticStyles.inputGroup}>
                            <label style={staticStyles.label}>운동 목표 (중복 선택 가능)</label>
                            <div style={staticStyles.purposeGrid}>
                                {purposeOptions.map((item) => {
                                    const isSelected = formData.goal.some(g => g.code === item.code);
                                    return (
                                        <button
                                            key={item.code}
                                            onClick={() => handlePurposeSelect(item)}
                                            style={isSelected ? staticStyles.purposeButtonActive : staticStyles.purposeButtonInactive}
                                        >
                                            {item.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 2: 운동 시간 */}
                {step === 2 && (
                    <div style={staticStyles.formContainer}>
                        <div style={staticStyles.inputGroup}>
                            <label style={staticStyles.label}>희망 운동 시간 (중복 선택 가능)</label>
                            <div style={staticStyles.purposeGrid}>
                                {timeOptions.map((item) => {
                                    const isSelected = formData.time.some(t => t.code === item.code);
                                    return (
                                        <button
                                            key={item.code}
                                            onClick={() => handleTimeSelect(item)}
                                            style={isSelected ? staticStyles.purposeButtonActive : staticStyles.purposeButtonInactive}
                                        >
                                            {item.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 3: 요청 사항 */}
                {step === 3 && (
                    <div style={staticStyles.formContainer}>
                        <div style={staticStyles.inputGroup}>
                            <label style={staticStyles.label}>추가 요청사항</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                placeholder="질병 및 유의사항이 있다면 적어주세요"
                                style={staticStyles.textarea}
                            />
                        </div>
                    </div>
                )}
            </main>

            {/* 하단 버튼 */}
            <div style={staticStyles.bottomButtonWrapper}>
                {step === 1 ? (
                    <button style={staticStyles.nextButton} onClick={handleNext}>다음</button>
                ) : step === 2 ? (
                    <div style={staticStyles.buttonRow}>
                        <button style={staticStyles.prevButton} onClick={() => setStep(1)}>이전</button>
                        <button style={staticStyles.submitButton} onClick={handleNext}>다음</button>
                    </div>
                ) : (
                    <div style={staticStyles.buttonRow}>
                        <button style={staticStyles.prevButton} onClick={() => setStep(2)}>이전</button>
                        <button style={staticStyles.submitButton} onClick={handleSubmit}>문의하기</button>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={modalConfig.isOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirmModal}
                title={modalConfig.title}
                message={modalConfig.message}
            />
        </div>
    );
}

const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%',
        zIndex: 100, backgroundColor: '#0D042D',
    },
    mainContent: {
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        paddingLeft: '24px', paddingRight: '24px', paddingBottom: '100px',
        scrollbarWidth: 'none', msOverflowStyle: 'none',
    },
    stepIndicatorContainer: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px',
    },
    stepCircle: {
        width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: 'Pretendard', fontWeight: '700', fontSize: '14px',
    },
    stepCircleActive: { background: '#8052FF', color: '#FFFFFF', border: 'none' },
    stepCircleInactive: { background: 'transparent', color: '#747474', border: '1px solid #3A394A' },
    stepLine: { width: '40px', height: '1px', background: '#3A394A', margin: '0 8px' },
    stepTitle: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '20px', color: '#FCFCFA', marginBottom: '24px',
    },
    formContainer: { display: 'flex', flexDirection: 'column', gap: '24px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: {
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '14px', color: '#9A9A9A',
    },
    textarea: {
        width: '100%', height: '300px', borderRadius: '8px', background: '#1B1931', border: '1px solid #3A394A',
        padding: '16px', color: '#FCFCFA', fontSize: '16px', fontFamily: 'Pretendard',
        boxSizing: 'border-box', resize: 'none',
    },
    purposeGrid: {
        width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px',
    },
    purposeButtonActive: {
        width: '100%', height: '60px', backgroundColor: '#1B1931', //paddingBottom: '10px',
        cursor: 'pointer', border: '1px solid #8052FF', borderRadius: '8px',
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '17px', color: '#FCFCFA',
    },
    purposeButtonInactive: {
        width: '100%', height: '60px', backgroundColor: '#1B1931',
        cursor: 'pointer', border: '1px solid #3A394A', borderRadius: '8px',
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '17px', color: '#747474',
    },
    bottomButtonWrapper: {
        position: 'fixed', bottom: 0, left: 0, width: '100%',
        padding: '24px 24px calc(env(safe-area-inset-bottom, 24px) + 24px) 24px',
        backgroundColor: '#0D042D', zIndex: 100, boxSizing: 'border-box',
    },
    nextButton: {
        width: '100%', height: '56px', borderRadius: '12px', border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: '#FFFFFF', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    },
    buttonRow: { display: 'flex', gap: '10px' },
    prevButton: {
        flex: 1, height: '56px', borderRadius: '12px', border: '1px solid #3A394A',
        background: '#212836', color: '#FFFFFF', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    },
    submitButton: {
        flex: 2, height: '56px', borderRadius: '12px', border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: '#FFFFFF', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    },
};

export default PtInquiry;